/**
 * Factory para recursos FHIR R5
 *
 * Objetivo:
 * - Centralizar as operações CRUD sobre qualquer recurso FHIR.
 * - Manter tipagem consistente usando o namespace `fhir5` do @types/fhir.
 * - Receber a URL base como parâmetro para suportar múltiplos servidores FHIR.
 *
 * Uso:
 *   const orgApi = createFhirResource<fhir5.Organization>('https://fhir.server/Organization');
 *   const all    = await orgApi.list();
 *   const found  = await orgApi.search({ name: 'Hospital' });
 *   const byPost = await orgApi.searchByBundle(requestBundle);
 *   const org    = await orgApi.read('123');
 */

import type { AxiosRequestConfig } from "axios";

import {
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
  httpPut,
} from "../httpClient";
import type { FhirBundle, FhirSearchParams } from "../types/fhir-types";

/**
 * JSON Patch (RFC 6902)
 *
 * Observação:
 * - Alguns backends (incluindo HAPI/implementações FHIR customizadas) exigem PATCH
 *   no formato JSON Patch (lista de operações) em vez de um objeto parcial.
 * - Para esse caso, o Content-Type deve ser `application/json-patch+json`.
 */
export type JsonPatchOperation =
  | { op: "add"; path: string; value: unknown }
  | { op: "replace"; path: string; value: unknown }
  | { op: "remove"; path: string }
  | { op: "move"; path: string; from: string }
  | { op: "copy"; path: string; from: string }
  | { op: "test"; path: string; value: unknown };

export type JsonPatchDocument = JsonPatchOperation[];

/**
 * Cria um cliente CRUD para um recurso FHIR específico.
 *
 * @param baseUrl - URL completa ou relativa do endpoint do recurso
 *   (ex.: 'https://fhir.example.com/Patient' ou '/admin/fhir/Patient').
 *   A factory NÃO monta a URL — ela é 100% controlada pelo chamador.
 *
 * @param defaultConfig - Configuração padrão de Axios para todas as operações
 *   desse recurso (headers, params, etc.). A cada chamada, o config passado
 *   será mesclado com esse default, permitindo sobrescritas pontuais.
 *
 * @returns Objeto com os 8 métodos FHIR padrão.
 */
export function createFhirResource<T extends fhir5.Resource>(
  baseUrl: string,
  defaultConfig?: AxiosRequestConfig,
) {
  /**
   * Mescla a configuração padrão com a configuração específica da chamada.
   *
   * - defaultConfig fornece valores base (ex.: headers comuns a todos os recursos).
   * - config da chamada pode sobrescrever qualquer campo.
   * - headers e params são mesclados de forma superficial.
   */
  function mergeConfig(
    config?: AxiosRequestConfig,
  ): AxiosRequestConfig | undefined {
    if (!defaultConfig) {
      return config;
    }

    if (!config) {
      return defaultConfig;
    }

    return {
      ...defaultConfig,
      ...config,
      headers: {
        ...defaultConfig.headers,
        ...config.headers,
      },
      params: {
        ...defaultConfig.params,
        ...config.params,
      },
    };
  }
  /**
   * list — GET {baseUrl}
   *
   * Retorna todos os recursos sem nenhum filtro.
   * Atalho explícito para `search()` sem parâmetros.
   *
   * Uso: orgApi.list()
   */
  function list(config?: AxiosRequestConfig): Promise<FhirBundle> {
    return httpGet<FhirBundle>(baseUrl, mergeConfig(config));
  }

  /**
   * search — GET {baseUrl}?params
   *
   * Busca recursos com parâmetros de query string.
   * Retorna um Bundle contendo os resultados filtrados.
   *
   * Uso: orgApi.search({ name: 'Hospital', _count: '10' })
   */
  function search(
    params?: FhirSearchParams,
    config?: AxiosRequestConfig,
  ): Promise<FhirBundle> {
    const merged = mergeConfig(config);
    return httpGet<FhirBundle>(baseUrl, {
      ...merged,
      params: {
        ...(merged?.params as any),
        ...params,
      },
    });
  }

  /**
   * searchByBundle — POST {baseUrl}/_search com Bundle
   *
   * Executa uma busca complexa enviando um Bundle como corpo da requisição.
   * Usado quando os parâmetros são muito grandes para query string,
   * ou quando o servidor exige busca via POST (ex.: batch/transaction).
   *
   * O Bundle de entrada deve ter type "batch" ou "transaction".
   * O servidor retorna um Bundle com os resultados correspondentes.
   *
   * Uso:
   *   const requestBundle: fhir5.Bundle = {
   *     resourceType: 'Bundle',
   *     type: 'batch',
   *     entry: [{ request: { method: 'GET', url: 'Patient?name=João' } }],
   *   };
   *   const results = await api.searchByBundle(requestBundle);
   */
  function searchByBundle(
    bundle: FhirBundle,
    config?: AxiosRequestConfig,
  ): Promise<FhirBundle> {
    const merged = mergeConfig(config);

    return httpPost<FhirBundle, FhirBundle>(`${baseUrl}/_search`, bundle, {
      ...merged,
      headers: {
        ...(merged?.headers || {}),
        // Content-Type obrigatório para operações com Bundle no padrão FHIR
        "Content-Type": "application/fhir+json",
      },
    });
  }

  /**
   * read — GET {baseUrl}/{id}
   *
   * Lê um recurso pelo ID lógico.
   * Retorna o recurso tipado como T.
   */
  function read(id: string, config?: AxiosRequestConfig): Promise<T> {
    return httpGet<T>(`${baseUrl}/${id}`, mergeConfig(config));
  }

  /**
   * create — POST {baseUrl}
   *
   * Cria um novo recurso no servidor FHIR.
   * Retorna o recurso criado (com id gerado pelo servidor).
   */
  function create(resource: T, config?: AxiosRequestConfig): Promise<T> {
    return httpPost<T, T>(baseUrl, resource, mergeConfig(config));
  }

  /**
   * update — PUT {baseUrl}/{id}
   *
   * Substitui completamente um recurso existente.
   * O recurso DEVE conter o campo `id`.
   */
  function update(
    id: string,
    resource: T,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return httpPut<T, T>(`${baseUrl}/${id}`, resource, mergeConfig(config));
  }

  /**
   * patch — PATCH {baseUrl}/{id}
   *
   * Atualiza parcialmente um recurso.
   * Envia JSON Patch (RFC 6902) para compatibilidade com backends que exigem
   * o formato de operações (em vez de um objeto parcial).
   */
  function patch(
    id: string,
    patchDocument: JsonPatchDocument,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const merged = mergeConfig(config);
    return httpPatch<T, JsonPatchDocument>(
      `${baseUrl}/${id}`,
      patchDocument,
      {
        ...merged,
        headers: {
          // Backend espera JSON Patch (RFC 6902)
          "Content-Type": "application/json-patch+json",
          // Permite override explícito via config.headers
          ...(merged?.headers || {}),
        },
      },
    );
  }

  /**
   * remove — DELETE {baseUrl}/{id}
   *
   * Remove um recurso pelo ID.
   * Retorna OperationOutcome quando o servidor responde com detalhes,
   * ou void quando a resposta é 204 No Content.
   *
   * Nomeado `remove` para evitar conflito com a keyword `delete` do JS.
   */
  function remove(
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<fhir5.OperationOutcome | void> {
    return httpDelete<fhir5.OperationOutcome | void>(
      `${baseUrl}/${id}`,
      mergeConfig(config),
    );
  }

  return { list, search, searchByBundle, read, create, update, patch, remove };
}

/**
 * Tipo utilitário que extrai o retorno da factory.
 * Útil para tipar variáveis que armazenam o cliente:
 *
 *   let patientApi: FhirResourceClient<fhir5.Patient>;
 */
export type FhirResourceClient<T extends fhir5.Resource> = ReturnType<
  typeof createFhirResource<T>
>;

/**
 * API FHIR de alto nível baseada em resourceType.
 *
 * Fornece métodos genéricos (`get`, `search`, `create`, etc.)
 * para qualquer recurso, reutilizando internamente `createFhirResource`.
 */
export interface FhirApi {
  get<T extends fhir5.Resource>(
    resourceType: string,
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<T>;

  search<T extends fhir5.Resource>(
    resourceType: string,
    params?: FhirSearchParams,
    config?: AxiosRequestConfig,
  ): Promise<FhirBundle>;

  create<T extends fhir5.Resource>(
    resourceType: string,
    resource: T,
    config?: AxiosRequestConfig,
  ): Promise<T>;

  update<T extends fhir5.Resource>(
    resourceType: string,
    id: string,
    resource: T,
    config?: AxiosRequestConfig,
  ): Promise<T>;

  patch<T extends fhir5.Resource>(
    resourceType: string,
    id: string,
    patchDocument: JsonPatchDocument,
    config?: AxiosRequestConfig,
  ): Promise<T>;

  remove<T extends fhir5.Resource>(
    resourceType: string,
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<fhir5.OperationOutcome | void>;

  /**
   * Acesso ao client FHIR baseado na factory genérica.
   * Útil quando você quer usar os métodos `list`, `read`, etc.
   * com um resourceType específico.
   */
  resource<T extends fhir5.Resource>(
    resourceType: string,
    defaultConfig?: AxiosRequestConfig,
  ): FhirResourceClient<T>;
}

/**
 * Cria uma API FHIR de alto nível baseada em um prefixo de path.
 *
 * Ex.:
 *   const adminFhirApi = createFhirApi("/admin/fhir");
 *   const patient = await adminFhirApi.get<fhir5.Patient>("Patient", "123");
 */
export function createFhirApi(
  basePath: string,
  defaultConfig?: AxiosRequestConfig,
): FhirApi {
  const baseClientFor = <T extends fhir5.Resource>(
    resourceType: string,
    resourceDefaultConfig?: AxiosRequestConfig,
  ) =>
    createFhirResource<T>(`${basePath}/${resourceType}`, {
      ...defaultConfig,
      ...resourceDefaultConfig,
      headers: {
        ...defaultConfig?.headers,
        ...resourceDefaultConfig?.headers,
      },
      params: {
        ...defaultConfig?.params,
        ...resourceDefaultConfig?.params,
      },
    });

  const api: FhirApi = {
    get<T extends fhir5.Resource>(
      resourceType: string,
      id: string,
      config?: AxiosRequestConfig,
    ): Promise<T> {
      return baseClientFor<T>(resourceType).read(id, config);
    },

    search<T extends fhir5.Resource>(
      resourceType: string,
      params?: FhirSearchParams,
      config?: AxiosRequestConfig,
    ): Promise<FhirBundle> {
      return baseClientFor<T>(resourceType).search(params, config);
    },

    create<T extends fhir5.Resource>(
      resourceType: string,
      resource: T,
      config?: AxiosRequestConfig,
    ): Promise<T> {
      return baseClientFor<T>(resourceType).create(resource, config);
    },

    update<T extends fhir5.Resource>(
      resourceType: string,
      id: string,
      resource: T,
      config?: AxiosRequestConfig,
    ): Promise<T> {
      return baseClientFor<T>(resourceType).update(id, resource, config);
    },

    patch<T extends fhir5.Resource>(
      resourceType: string,
      id: string,
      patchDocument: JsonPatchDocument,
      config?: AxiosRequestConfig,
    ): Promise<T> {
      return baseClientFor<T>(resourceType).patch(id, patchDocument, config);
    },

    remove<T extends fhir5.Resource>(
      resourceType: string,
      id: string,
      config?: AxiosRequestConfig,
    ): Promise<fhir5.OperationOutcome | void> {
      return baseClientFor<T>(resourceType).remove(id, config);
    },

    resource<T extends fhir5.Resource>(
      resourceType: string,
      resourceDefaultConfig?: AxiosRequestConfig,
    ): FhirResourceClient<T> {
      return baseClientFor<T>(resourceType, resourceDefaultConfig);
    },
  };

  return api;
}
